/**
 * @file  src\components\Inventory\CreateInventory\InventoryPropertyTypes.js
 * @brief  This file is responsible for inventory property types in inventory creation
 * @date   April, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useState, useEffect } from "react";
import { getPropertyTypes } from "../../../services/commonServices";
import { handleRadioButonChange } from "../../../actions/common";
import { usePrevious } from "../../../hooks/usePrevious";
import {
  BUY,
  ENTIRE_SPACE_INVENTY_TYPE,
  IS_ENTIRE_PLACE,
  IS_PRIVATE_ROOM,
  PRIVATE_ROOM_INVENTY_TYPE,
  INVENTORY_TYPE,
} from "../../../constants/common";
import { convertToNumber } from "../../../helpers/validations";

const InventoryPropertyTypes = (props) => {
  const {
    handleContinueButtonState,
    handleFormValues,
    propertyType,
    adType,
    prptySubType,
    clearPreviousStates,
    isEdit = false,
  } = props;
  const [propertyList, setPropertyList] = useState([]);
  const [propertySubItems, setPropertySubItems] = useState([]);
  useEffect(() => {
    if (propertyType) {
      if (propertyType === IS_ENTIRE_PLACE) {
        getProperiesSubItems(ENTIRE_SPACE_INVENTY_TYPE);
      } else if (propertyType === IS_PRIVATE_ROOM) {
        getProperiesSubItems(PRIVATE_ROOM_INVENTY_TYPE);
      } else {
        handleContinueButtonState();
      }
    } else {
      const isDisabled = true;
      handleContinueButtonState(isDisabled);
    }

    handlePropertySubTypes();
    getProperties(INVENTORY_TYPE);
  }, []);
  /**
   * To get property types selection
   * @param {*} adType
   */
  const getProperties = async (adType) => {
    await getPropertyTypes(adType)
      .then((response) => {
        if (response.errorCode === 0 && _.get(response, "result", null)) {
          const propertyTypes = _.get(response, "result", null);
          setPropertyList(propertyTypes);
        }
      })
      .catch((err) => {
        console.log("Error", err);
      });
  };
  /**
   * To handle property subtypes
   */
  const handlePropertySubTypes = () => {
    if (propertyType === IS_ENTIRE_PLACE || propertyType === IS_PRIVATE_ROOM) {
      if (prptySubType) {
        handleContinueButtonState();
      } else {
        const isDisabled = true;
        handleContinueButtonState(isDisabled);
      }
    }
  };
  useEffect(() => {
    handlePropertySubTypes();
  }, [propertySubItems, prptySubType]);
  /**
   * To fetch sub property types
   * @param {*} adType
   */
  const getProperiesSubItems = async (adType) => {
    await getPropertyTypes(adType)
      .then((response) => {
        if (response.errorCode === 0 && _.get(response, "result", null)) {
          const propertyTypes = _.get(response, "result", null);
          setPropertySubItems(propertyTypes);
        }
      })
      .catch((err) => {
        console.log("Error", err);
      });
  };

  const previousValue = usePrevious(propertyType);
  const previousSubTypeValue = usePrevious(prptySubType);
  /**
   * To handle property selection
   * @param {*} e
   * @param {*} typeText
   */
  const handlePropertyClick = (e, typeText) => {
    const isDisabled = true;
    handleContinueButtonState(isDisabled);
    const value = handleRadioButonChange(e);
    if (value) {
      if (convertToNumber(value) === IS_ENTIRE_PLACE) {
        getProperiesSubItems(ENTIRE_SPACE_INVENTY_TYPE);
      } else if (convertToNumber(value) === IS_PRIVATE_ROOM) {
        getProperiesSubItems(PRIVATE_ROOM_INVENTY_TYPE);
      } else {
        handleContinueButtonState();
        setPropertySubItems([]);
      }
      clearPreviousSelectedValues(value);
      handleFormValues({
        prprtyType: value,
        prptyTypeTxt: typeText,
        prptySubType: "",
      });
    }
  };
  /**
   * To handle sub property selection
   * @param {*} e
   */
  const handlePropertySubItemClick = (e) => {
    const value = handleRadioButonChange(e);
    if (value) {
      handleContinueButtonState();
      clearPreviousSelectedValues(value);
      handleFormValues({ prptySubType: value });
    }
  };

  const clearPreviousSelectedValues = (currentSelectedValue) => {
    const isClearPrevious =
      convertToNumber(previousValue) !==
        convertToNumber(currentSelectedValue) ||
      convertToNumber(previousSubTypeValue) !==
        convertToNumber(currentSelectedValue);
    if (isClearPrevious) {
      clearPreviousStates();
    }
  };
  /**
   * To load property types
   * @returns
   */
  const loadPropertyTypes = () => {
    const List =
      propertyList &&
      propertyList.map((pt) => {
        const typeId = _.get(pt, "PropertyTypeId", "");
        const typeText = _.get(pt, "PropertyTypeText", "");
        const Img = _.get(pt, "Img", "");
        const Desc = _.get(pt, "Description", "");
        return (
          <div
            key={typeId}
            className={
              !isEdit
                ? "col-sm-12 col-lg-12 col-xl-12 col-xxl-6"
                : "col-sm-12 col-lg-12 col-xl-12 col-xxl-12"
            }
          >
            <label className="radio-wrap mb-3">
              <input
                type="radio"
                name="radio"
                value={typeId ? typeId : ""}
                checked={(propertyType && propertyType == typeId) || ""}
                onChange={(e) => handlePropertyClick(e, typeText)}
              />
              <span>
                <div className="wantad-type-info">
                  {adType !== BUY && Desc ? (
                    <>
                      {" "}
                      <h2>{_.get(pt, "PropertyTypeText", "")}</h2> <p>{Desc}</p>{" "}
                    </>
                  ) : (
                    <h2 className="pb-0">
                      {_.get(pt, "PropertyTypeText", "")}
                    </h2>
                  )}
                </div>
              </span>
            </label>
          </div>
        );
      });

    return List;
  };
  /**
   * To load property sub types
   * @returns
   */
  const loadPropertySubTypes = () => {
    const List =
      propertySubItems &&
      propertySubItems.map((pt) => {
        const typeId = _.get(pt, "PropertyTypeId", "");
        return (
          <div key={typeId} className="col-sm-12 col-lg-12 col-xl-12 col-xxl-6">
            <label className="radio-wrap mb-3">
              <input
                type="radio"
                name="pst-radio"
                id={typeId}
                value={typeId ? typeId : ""}
                checked={(prptySubType && prptySubType == typeId) || ""}
                onChange={(e) => handlePropertySubItemClick(e)}
              />
              <span>
                <div className="wantad-type-info">
                  <h2 className="pb-0">{_.get(pt, "PropertyTypeText", "")}</h2>
                </div>
              </span>
            </label>
          </div>
        );
      });

    return List;
  };

  return (
    <div className={!isEdit ? "center-content-sec mw-100" : ""}>
      <div className="wantad-type-wrap pb-24">
        <div className="row">{loadPropertyTypes()}</div>
      </div>
      {propertySubItems && propertySubItems.length > 0 ? (
        <div className="wantad-type-wrap select-type-wrap">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <h6 className="sub-head mb-0">Select type{isEdit && "*"}:</h6>
          </div>
          <div className="row">{loadPropertySubTypes()}</div>
        </div>
      ) : (
        ""
      )}
    </div>
  );
};
export default InventoryPropertyTypes;
